import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthGuard } from '@/guards';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { useChatContext } from '@/contexts/ChatContext';
import { getDoc, updateDoc, deleteDoc, duplicateDoc, createDoc, type Doc } from '@/api/docs';
import { trackCreateDoc } from '@/lib/analytics';
import { DocsHeader } from '@/components/docs/DocsHeader';
import { SideChat } from '@/components/docs/SideChat';
import { RichTextEditor } from '@/components/docs/RichTextEditor';
import { markdownToHtml, looksLikeHtml } from '@/utils/docContent';
import { ArrowCounterClockwise, Flask } from 'phosphor-react';

/** Mock de versões do documento para simular o histórico e testar "Voltar" */
const MOCK_DOC_VERSIONS: string[] = [
  '<p class="m-0 theme-text leading-normal">Versão inicial do documento (v1).</p>',
  '<p class="m-0 theme-text leading-normal">Versão inicial do documento (v1).</p><p class="m-0 theme-text leading-normal">Segundo parágrafo adicionado (v2).</p>',
  '<h1 class="text-3xl font-bold mb-4 mt-6 theme-text leading-tight">Título do documento</h1><p class="m-0 theme-text leading-normal">Versão inicial do documento (v1).</p><p class="m-0 theme-text leading-normal">Segundo parágrafo adicionado (v2).</p><p class="m-0 theme-text leading-normal">Terceiro parágrafo e título inseridos (v3).</p>',
  '<h1 class="text-3xl font-bold mb-4 mt-6 theme-text leading-tight">Título do documento</h1><p class="m-0 theme-text leading-normal">Versão inicial do documento (v1).</p><p class="m-0 theme-text leading-normal">Segundo parágrafo adicionado (v2).</p><p class="m-0 theme-text leading-normal">Terceiro parágrafo e título inseridos (v3).</p><p class="m-0 theme-text leading-normal"><strong class="font-bold">Versão atual (v4)</strong> — use &quot;Voltar&quot; para navegar nas versões anteriores.</p>',
];

export default function SpacePage() {
  return (
    <AuthGuard>
      <SpaceView />
    </AuthGuard>
  );
}

function SpaceView() {
  const params = useParams<{ docsId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { selectChat, clearChat } = useChatContext();

  const spaceId = params?.docsId ? String(params.docsId) : '';
  const [space, setSpace] = useState<Doc | null>(null);
  const [loading, setLoading] = useState(true);
  const [editedContent, setEditedContent] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [editorKey, setEditorKey] = useState(0);
  const contentRef = useRef<string>('');
  const lastPushedRef = useRef<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUndoRedoRef = useRef(false);
  const contentAtBurstStartRef = useRef<string>('');

  const [isSideChatOpen, setIsSideChatOpen] = useState(true);

  const MAX_HISTORY = 50;
  const DEBOUNCE_MS = 1200;

  const pushToHistory = useCallback((previousContent: string) => {
    if (isUndoRedoRef.current) return;
    if (previousContent === lastPushedRef.current) return;
    lastPushedRef.current = previousContent;
    setHistory((h) => [...h, previousContent].slice(-MAX_HISTORY));
  }, []);

  const handleContentChange = useCallback((newContent: string) => {
    if (!debounceRef.current) {
      contentAtBurstStartRef.current = contentRef.current;
    }
    contentRef.current = newContent;
    setEditedContent(newContent);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      pushToHistory(contentAtBurstStartRef.current);
      debounceRef.current = null;
    }, DEBOUNCE_MS);
  }, [pushToHistory]);

  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    isUndoRedoRef.current = true;
    setEditedContent(prev);
    contentRef.current = prev;
    lastPushedRef.current = prev;
    contentAtBurstStartRef.current = prev;
    setHistory((h) => h.slice(0, -1));
    setTimeout(() => { isUndoRedoRef.current = false; }, 0);
  }, [history.length]);

  const canUndo = history.length > 0;

  const handleSimulateHistory = useCallback(() => {
    if (MOCK_DOC_VERSIONS.length < 2) return;
    (document.activeElement as HTMLElement)?.blur?.();
    const current = MOCK_DOC_VERSIONS[MOCK_DOC_VERSIONS.length - 1];
    const previousVersions = MOCK_DOC_VERSIONS.slice(0, -1);
    isUndoRedoRef.current = true;
    setEditedContent(current);
    setHistory(previousVersions);
    setEditorKey((k) => k + 1);
    contentRef.current = current;
    lastPushedRef.current = previousVersions[previousVersions.length - 1];
    contentAtBurstStartRef.current = current;
    setTimeout(() => { isUndoRedoRef.current = false; }, 0);
    toast.success('Mock ativo', `${previousVersions.length} versões anteriores carregadas. Use "Voltar" para simular.`);
  }, [toast]);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!spaceId) return;
      setLoading(true);
      try {
        const data = await getDoc(spaceId);
        if (!alive) return;
        setSpace(data);
        // Build initial HTML: from sections (legacy markdown) or rawMarkdown (HTML or legacy markdown)
        let initialHtml = '';
        if (Array.isArray(data.sections) && data.sections.length > 0) {
          const fullMarkdown = data.sections
            .map(s => {
              const titleLine = `# ${s.title}`;
              const contentText = s.content.trim();
              return contentText ? `${titleLine}\n\n${contentText}` : titleLine;
            })
            .join('\n\n');
          initialHtml = markdownToHtml(fullMarkdown);
        } else {
          const raw = data.rawMarkdown || '';
          initialHtml = looksLikeHtml(raw) ? raw : (raw ? markdownToHtml(raw) : '');
        }
        setEditedContent(initialHtml);
        setHistory([]);
        contentRef.current = initialHtml;
        lastPushedRef.current = initialHtml;
        contentAtBurstStartRef.current = initialHtml;

        // Load chat history if chatId exists
        if (data.chatId) {
          try {
            await selectChat(data.chatId);
          } catch (chatError) {
            console.warn('Failed to load chat history:', chatError);
            // Don't show error to user, just log it
          }
        }
      } catch (e) {
        if (!alive) return;
        toast.error('Não foi possível abrir o documento');
        navigate('/dashboard');
      } finally {
        if (alive) setLoading(false);
      }
    };
    void run();
    return () => {
      alive = false;
    };
  }, [navigate, spaceId, toast, selectChat]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleCopyLink = async () => {
    try {
      if (typeof window === 'undefined') return;
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Copiado', 'Link do documento copiado.');
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  // CRUD Handlers
  const handleCreateNew = async () => {
    try {
      // Clear the current chat
      clearChat();

      // Create a new doc with 'Rascunho' title
      const newDoc = await createDoc({
        title: 'Rascunho',
      });

      trackCreateDoc();
      toast.success('Novo documento criado');
      navigate(`/docs/${newDoc.id}`);
    } catch {
      toast.error('Não foi possível criar o documento');
    }
  };

  const handleDuplicate = async () => {
    if (!spaceId) return;
    try {
      const duplicated = await duplicateDoc(spaceId);
      toast.success('Documento duplicado com sucesso');
      navigate(`/docs/${duplicated.id}`);
    } catch {
      toast.error('Não foi possível duplicar o documento');
    }
  };

  const handleRename = async (newTitle: string) => {
    if (!spaceId || !newTitle.trim()) return;
    try {
      const updated = await updateDoc(spaceId, { title: newTitle.trim() });
      setSpace(updated);
      toast.success('Documento renomeado');
    } catch {
      toast.error('Não foi possível renomear o documento');
    }
  };

  const handleDelete = async () => {
    if (!spaceId) return;
    if (!window.confirm('Tem certeza que deseja mover este documento para a lixeira?')) return;
    try {
      await deleteDoc(spaceId);
      toast.success('Documento movido para a lixeira');
      navigate('/dashboard');
    } catch {
      toast.error('Não foi possível excluir o documento');
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };







  const displayName = user?.name || user?.email || 'Usuário';
  const initials = (displayName || 'U')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');


  return (
    <div className="relative min-h-screen bg-[color:var(--background)] text-[color:var(--foreground)]">
      {/* Top / bottom fades */}
      <div className="fixed left-0 top-0 right-0 z-10 h-32 pointer-events-none bg-gradient-to-b from-[color:var(--background)] from-60% to-transparent max-md:h-20" />
      <div className="fixed left-0 bottom-0 right-0 z-10 h-32 pointer-events-none bg-gradient-to-t from-[color:var(--background)] from-60% to-transparent max-md:h-20" />

      <DocsHeader
        docId={spaceId}
        docTitle={space?.title}
        displayName={displayName}
        initials={initials}
        onCopyLink={handleCopyLink}
        isSideChatOpen={isSideChatOpen}
        onToggleSideChat={() => setIsSideChatOpen(true)}
        onCreateNew={handleCreateNew}
        onDuplicate={handleDuplicate}
        onRename={handleRename}
        onDelete={handleDelete}
        onBack={handleBack}
      />

      {/* Content */}
      <main className={`relative z-0 pt-20 pb-0 max-md:pt-20 transition-all duration-300 h-[calc(100vh-5rem)] max-md:h-[calc(100vh-5rem)] ${isSideChatOpen ? 'pr-[420px] max-md:pr-0' : 'pr-0'}`}>
        {loading && (
          <div className="flex items-center justify-center h-full gap-3 theme-text-muted">
            <div className="w-5 h-5 border-2 border-[color:var(--brand-500)] border-t-transparent rounded-full animate-spin" />
            Carregando documento…
          </div>
        )}

        {!loading && space && (
          <>
            <div className="absolute left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3 py-2 rounded-lg border theme-border theme-bg shadow-lg" style={{ top: '6.5rem' }}>
              {canUndo ? (
                <>
                  <span className="text-xs font-medium theme-text-muted tabular-nums">
                    {history.length} {history.length === 1 ? 'versão anterior' : 'versões anteriores'}
                  </span>
                  <div className="w-px h-4 theme-border" />
                  <button
                    type="button"
                    onClick={handleUndo}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors"
                    title="Desfazer (voltar à versão anterior)"
                  >
                    <ArrowCounterClockwise className="w-3.5 h-3.5" weight="bold" />
                    Voltar
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleSimulateHistory}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium theme-text-muted hover:theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors border border-dashed theme-border"
                  title="Carregar versões mock para testar o histórico e Voltar"
                >
                  <Flask className="w-3.5 h-3.5" weight="bold" />
                  Simular histórico
                </button>
              )}
            </div>
            <RichTextEditor
              key={`doc-editor-${editorKey}`}
              value={editedContent}
              onChange={handleContentChange}
              placeholder="Digite o conteúdo do documento..."
              minHeight="100%"
            />
          </>
        )}
      </main>

      {/* Side Chat */}
      <SideChat isOpen={isSideChatOpen} onClose={() => setIsSideChatOpen(false)} docTitle={space?.title} />
    </div>
  );
}
